import { ModalAction } from '../ModalAction'
import { useCoachingString } from '../useCoachingString'
import { friendlyErrorMessage } from '@/core/lib/dbError'
import { ApiError, assertWriteIdentity, captureWriteIdentity } from '@/core/api/http'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useApp } from '@/core/state/appStore'
import { abschlussberichtForm } from '../../../templates'
import { updateFormField, initialFormValues, type FormValues } from '@/core/components/form/formModel'
import { FormRenderer } from '@/core/components/form/FormRenderer'
import { gateAdvance } from '@/core/lib/advanceGate'
import { useAutosave } from '@/core/hooks/useAutosave'
import { filterBerichtTemplate } from '../../../lib/berichtTemplate'
import {
  loadBerichtForm,
  saveBerichtDraft,
  checkBericht,
  generateBericht,
  confirmBericht,
  getBerichtPath,
  getBerichtBehoerde,
  berichtManualFields,
} from '../../../flows/abschlussbericht'
import { handleFailure } from '@/core/lib/toast'
import { ModalHeader } from '@/core/components/modal/ModalHeader'
import { LoadingState, ScrollBar, toastError, toastSuccess } from 'admination-react'
import { PdfRegion } from '@/core/components/modal/PdfRegion'

export function AbschlussberichtDokument({ coachingId }: { coachingId: string | null }) {
  const { state, dispatch, reloadDashboard } = useApp()
  const scrollerRef = useRef<HTMLElement>(null)
  const [values, setValues] = useState<FormValues | null>(null)
  const [attempted, setAttempted] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const busy = generating || confirming
  const submitting = useRef(false)
  const operation = useRef(0)
  const alive = useRef(true)
  useLayoutEffect(() => {
    alive.current = true
    submitting.current = false
    setGenerating(false)
    setConfirming(false)
    return () => {
      alive.current = false
      operation.current += 1
    }
  }, [coachingId, state.screen?.tab])

  function current(nr: number) { return alive.current && operation.current === nr }

  const [behoerde] = useCoachingString(coachingId, getBerichtBehoerde)
  const ready = values !== null && behoerde !== undefined
  const resolvedBehoerde = behoerde ?? null

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const saved = coachingId ? await loadBerichtForm(coachingId) : null
      const init = initialFormValues(abschlussberichtForm, saved)
      if (!cancelled) {
        setValues(init)
        setAttempted(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [coachingId])

  const template = useMemo(
    () => filterBerichtTemplate(abschlussberichtForm, resolvedBehoerde),
    [resolvedBehoerde],
  )
  const manualFields = useMemo(() => berichtManualFields(resolvedBehoerde), [resolvedBehoerde])
  const check = useMemo(
    () => (values ? checkBericht(values, resolvedBehoerde) : null),
    [values, resolvedBehoerde],
  )

  const handleChange = useCallback((path: string, v: unknown) => {
    if (!ready || submitting.current) return
    setValues((prev) => updateFormField(prev, path, v))
  }, [ready])

  const save = useCallback(
    (v: FormValues) => {
      if (!coachingId) return Promise.resolve()
      return saveBerichtDraft(coachingId, v).catch((error) => {
        console.error('Abschlussbericht speichern fehlgeschlagen:', error)
        throw error
      })
    },
    [coachingId],
  )
  const { flush } = useAutosave(values, ready && !!coachingId && !busy, (v) =>
    v ? save(v) : Promise.resolve(),
  250, { form: "AbschlussberichtDokument", record: coachingId ?? undefined, label: 'Abschlussbericht', recover: setValues })

  const [path] = useCoachingString(coachingId, getBerichtPath, !generating)

  function advance() {
    if (!ready || !coachingId || !values || submitting.current) return
    setAttempted(true)
    gateAdvance(check, async () => {
      submitting.current = true
      const nr = ++operation.current
      const identity = captureWriteIdentity()
      setGenerating(true)
      try {
        try { await flush() } catch (error) {
          assertWriteIdentity(identity)
          if (error instanceof ApiError && (error.status === 401 || error.status === 403)) throw error
        }
        await flush()
        assertWriteIdentity(identity)
        if (!current(nr)) return
        const result = await generateBericht(coachingId, values, identity)
        assertWriteIdentity(identity)
        if (!current(nr)) return
        if (result.ok) {
          toastSuccess('Der Abschlussbericht wurde erfolgreich erstellt.')
          reloadDashboard()
          dispatch({ type: "CHANGE_TAB", tab: 'pruefen' })
        } else handleFailure(result)
      } catch (error) {
        if (current(nr)) toastError(friendlyErrorMessage(error, 'Abschlussbericht erstellen fehlgeschlagen. Bitte versuchen Sie es erneut.'))
      } finally {
        if (current(nr)) {
          submitting.current = false
          setGenerating(false)
        }
      }
    })
  }

  async function saveClose() {
    if (!coachingId || submitting.current) return
    submitting.current = true
    const nr = ++operation.current
    const identity = captureWriteIdentity()
    setConfirming(true)
    try {
      await flush()
      assertWriteIdentity(identity)
      if (!current(nr)) return
      const result = await confirmBericht(coachingId, identity)
      assertWriteIdentity(identity)
      if (!current(nr)) return
      if ('ok' in result) handleFailure(result)
      else {
        reloadDashboard()
        dispatch({ type: "CHANGE_TAB", tab: 'teilnehmendenliste' })
      }
    } catch {
      if (current(nr)) toastError('Abschlussbericht bestätigen fehlgeschlagen.')
    } finally {
      if (current(nr)) {
        submitting.current = false
        setConfirming(false)
      }
    }
  }

  if (state.screen?.tab === 'pruefen' || generating) {
    return (
      <>
        <ModalHeader size="document" closeDisabled={busy}>
          <div className="pill-cluster">
            <ModalAction
              icon="i-check"
              primaryAction
              title={generating ? 'Dokument erstellen' : 'Bestätigen'}
              loading={busy}
              disabled={busy}
              onClick={saveClose}
            />
          </div>
        </ModalHeader>
        <PdfRegion
          path={path ?? null}
          waiting={generating || path === undefined}
          loadingLabel={generating ? 'Abschlussbericht wird erstellt …' : 'Abschlussbericht wird geladen …'}
          placeholder="Noch kein Abschlussbericht vorhanden — bitte zuerst das Formular ausfüllen."
        />
      </>
    )
  }

  return (
    <>
      <ModalHeader size="form" closeDisabled={busy}>
        <div className="pill-cluster">
          <ModalAction
            icon="i-doc"
            primaryAction
            title="Dokument erstellen"
            loading={busy}
            disabled={!ready || busy}
            onClick={advance}
          />
        </div>
      </ModalHeader>
      <main className="form" data-scroll-host ref={scrollerRef}>
        {!ready && <LoadingState>Abschlussbericht wird geladen …</LoadingState>}
        {ready && values && (
          <FormRenderer
              definition={template}
              values={values}
              onChange={handleChange}
              errors={check?.errors}
              attemptedAdvance={attempted}
              field={(field) => ({
                required: manualFields?.has(field.id),
                missing: manualFields?.has(field.id) ? 'mark' : undefined,
              })}
            />
        )}
      </main>
      <ScrollBar scrollerRef={scrollerRef} />
    </>
  )
}
